import { navigate } from "gatsby";
import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import Layout from "../../components/layouts/layout";
import DataService from "../../services/api/data_services";
import { BASE_SITE_URL } from "../../shared/constants";
import ArticleBlog from "../../components/articleBlog";
import TableContent from "../../components/articleBlog/tableContent";
import "../../components/skeletonLoader/index.scss";

const Article = ({ pageContext: { article: buildTimeArticle } }) => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(6);
  const [articleRawData, setArticleRawData] = useState([]);
  const [articleData, setArticleData] = useState([]);
  const [articleContent, setArticleContent] = useState([]);
  const [articleTag, setArticleTag] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const queryString =
    typeof window !== "undefined" ? window.location.href.split("/")[4] : "";
  const [isHover, setIsHover] = useState(false);
  const toggleHover = () => {
    setIsHover(!isHover);
  };
  // custom hook for getting previous value
  function usePrevious(value) {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }
  const prevCount = usePrevious(articleData);
  useEffect(() => {
    setLimit(6);
    setPage(1);
    setArticleData([]);
    setArticleRawData([]);
    DataService.getData(`/admin/api/post-details?slug=${queryString}`).then(
      (response) => {
        fetchRelatedArtciles(response.data.tag_lists, response.data.id);
        const articleContent = response.data;
        setArticleContent(articleContent);
        setArticleTag(response.data.article_tag);
        setIsLoading(false);
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString]);
  useEffect(() => {
    DataService.getData(`/admin/api/post-details?slug=${queryString}`).then(
      (response) => {
        fetchRelatedLoadmoreArtciles(response.data.tag_lists, response.data.id);
        const articleContent = response.data;
        setArticleContent(articleContent);
        setArticleTag(response.data.article_tag);
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, page]);
  // console.log("articleContent :>> ", articleContent);
  const fetchRelatedLoadmoreArtciles = (queryVal, id) => {
    let value = queryVal?.map((val) => `article_type[]=${val}`).join("&");
    DataService.getData(
      `/admin/api/article-tag-multiple?limit=${limit}&page=${page}&current_id=${id}&type=Article&${value}`,
    ).then((response) => {
      setArticleData([...articleData, ...response.data]);
      setArticleRawData(response);
    });
  };
  const fetchRelatedArtciles = (queryVal, id) => {
    let value = queryVal?.map((val) => `article_type[]=${val}`).join("&");
    DataService.getData(
      `/admin/api/article-tag-multiple?limit=${limit}&page=${1}&current_id=${id}&type=Article&${value}`,
    )
      .then((response) => {
        if (prevCount === undefined || response.data) {
          setArticleData(response.data);
          setArticleRawData(response);
        } else {
          setArticleData([...articleData, ...response.data]);
          setArticleRawData(response);
        }
      })
      .catch((error) => {
        navigate(-1);
      });
  };

  const [processedContent, setProcessedContent] = useState("");

  useEffect(() => {
    if (articleContent?.body_content) {
      const updatedContent = addHeadingIds(articleContent?.body_content);
      setProcessedContent(updatedContent);
    }
  }, [articleContent?.body_content]);

  const addHeadingIds = (htmlContent) => {
    let index = 0; // Unique ID counter

    return htmlContent.replace(
      /<b>(#{2,3})\s*([^<]+)<\/b>/g,
      (_, hashes, text) => {
        const level = hashes.length; // ## -> h2, ### -> h3
        const id = `heading-${index++}`;
        return `<h${level} class="toc${level} ${
          level === 2 ? "title-underline" : ""
        }" id="${id}">${text.trim().replace(/^#{2,3}\s*/, "")}</h${level}>`;
      },
    );
  };

  return (
    <Layout
      seo={{
        // Fall back to build-time pageContext data (available immediately,
        // pre-hydration) while the client-side post-details fetch resolves.
        title: articleContent?.title || buildTimeArticle?.title,
        ogtitle: articleContent?.og_title || buildTimeArticle?.og_title,
        description:
          articleContent?.meta_description || buildTimeArticle?.meta_description,
        ogurl: `${BASE_SITE_URL}/article/${
          articleContent?.slug || buildTimeArticle?.slug
        }/`,
        ogimage: articleContent?.og_image || buildTimeArticle?.og_image,
      }}
    >
      {(articleContent?.title || buildTimeArticle?.title) && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              headline: articleContent?.title || buildTimeArticle?.title,
              author: { "@type": "Organization", name: "DigiRyte" },
              datePublished:
                articleContent?.published_date ||
                buildTimeArticle?.published_date,
              dateModified:
                articleContent?.published_date ||
                buildTimeArticle?.published_date,
              publisher: { "@type": "Organization", name: "DigiRyte" },
            })}
          </script>
        </Helmet>
      )}
      {isLoading ? (
        <>
          {/* Hero skeleton */}
          <div className="abh-skl">
            <div className="abh-skl__inner">
              <div className="skl skl--dark abh-skl__badge" />
              <div className="skl skl--dark abh-skl__title-1" />
              <div className="skl skl--dark abh-skl__title-2" />
              <div className="abh-skl__meta">
                <div className="skl skl--dark abh-skl__avatar" />
                <div className="abh-skl__meta-lines">
                  <div className="skl skl--dark abh-skl__meta-line-1" />
                  <div className="skl skl--dark abh-skl__meta-line-2" />
                </div>
              </div>
            </div>
          </div>
          {/* Body skeleton */}
          <div className="art-body-skl">
            <div className="art-body-skl__main">
              {[
                "full",
                "lg",
                "md",
                "full",
                "lg",
                "full",
                "gap",
                "full",
                "md",
                "lg",
                "full",
                "sm",
                "gap",
                "full",
                "lg",
                "full",
                "md",
              ].map((s, i) =>
                s === "gap" ? (
                  <div key={i} className="art-body-skl__gap" />
                ) : (
                  <div
                    key={i}
                    className={`skl art-body-skl__line art-body-skl__line--${s}`}
                  />
                ),
              )}
            </div>
            <div className="art-body-skl__aside">
              <div className="skl art-body-skl__aside-title" />
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <div key={n} className="skl art-body-skl__aside-line" />
              ))}
            </div>
          </div>
        </>
      ) : (
        <>
          {articleContent && articleContent.body_content && (
            <ArticleBlog objectVal={articleContent} />
          )}
          <div className="article-page-body body-container">
            <TableContent
              articleContent={articleContent}
              objectData={articleData}
              content={processedContent}
            />
          </div>
        </>
      )}

      {/* Modern CTA strip */}
      <div className="article-cta-strip">
        <div className="article-cta-strip__inner body-container">
          <div className="article-cta-strip__text">
            <h2>Ready to accelerate your technology journey?</h2>
            <p>
              Talk to our experts about how digiRyte can deliver enterprise
              outcomes at AI-native speed.
            </p>
          </div>
          <a href="/contact-us" className="article-cta-strip__btn">
            Get in Touch
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M3 8h10M9 4l4 4-4 4"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </a>
        </div>
      </div>
    </Layout>
  );
};
export default Article;
